import { useCallback, useState } from "react";

import { apiError } from "@/shared/api/client";

/**
 * At most one mutation in flight on this appointment (#18807).
 *
 * <p>Deliberately NOT called "the room is busy". The server already owns every real queue
 * invariant — {@code callNext} claims a patient with {@code FOR UPDATE SKIP LOCKED}, and recall /
 * hold / skip are single-row updates Postgres serialises on its own. There is no room-level lock
 * for a client flag to enforce, and pretending otherwise would make a slow network look like a
 * locked room.
 *
 * <p>What this does own is narrower and still worth having: a doctor must not be able to press
 * "Tạm giữ" and "Hoàn tất & gọi tiếp" in the same second and leave the two outcomes racing. That is
 * an affordance about one appointment, and it lives in one place so a panel can consume the rule
 * without being able to define it.
 *
 * <p>See {@code docs/architecture/queue-action-busy-ownership.md} for the reasoning, including why
 * a real "room is locked" feature would belong in domain-scheduling and not here.
 */
export interface AppointmentActionGuard {
  /** A mutation on this appointment is in flight. */
  busy: boolean;
  /** The outcome of the last action, success or failure, for the caller to display. */
  message: string | null;
  /** Runs {@code fn}, reporting {@code okMessage} on success and the API detail on failure. */
  run: (fn: () => Promise<unknown>, okMessage: string) => Promise<void>;
  /** Clears the message — for a caller starting an unrelated action. */
  clear: () => void;
  /** Sets the message without running anything, e.g. the sign flow reporting its own call-next. */
  report: (message: string | null) => void;
}

export function useAppointmentActionGuard(
  appointmentId: string | null | undefined
): AppointmentActionGuard {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const run = useCallback(
    async (fn: () => Promise<unknown>, okMessage: string) => {
      if (!appointmentId) return;
      setBusy(true);
      setMessage(null);
      try {
        await fn();
        setMessage(okMessage);
      } catch (e) {
        // The failure is shown where the success would have been, rather than in the shared error
        // line: it belongs to the button that was pressed.
        setMessage(`Không thực hiện được: ${apiError(e).detail}`);
      } finally {
        setBusy(false);
      }
    },
    [appointmentId]
  );

  const clear = useCallback(() => setMessage(null), []);
  const report = useCallback((m: string | null) => setMessage(m), []);

  return { busy, message, run, clear, report };
}
